/**
 * Route Preloader Utility
 * Intelligently preloads route chunks for instant navigation
 */

// Cache to track which routes have been preloaded
const preloadedRoutes = new Set();

// Map of route names to their lazy component imports
const routeImports = {
  login: () => import('../pages/Login'),
  register: () => import('../pages/Register'),
  home: () => import('../pages/Home'),
  locations: () => import('../pages/Locations'),
  locationDetail: () => import('../pages/LocationDetail'),
  events: () => import('../pages/Events'),
  eventDetail: () => import('../pages/EventDetail'),
  createEvent: () => import('../pages/CreateEvent'),
  challenges: () => import('../pages/Challenges'),
  challengeDetail: () => import('../pages/ChallengeDetail'),
  createChallenge: () => import('../pages/CreateChallenge'),
  profile: () => import('../pages/UserProfile'),
  leaderboard: () => import('../pages/Leaderboard'),
  badges: () => import('../pages/Badges'),
  pointHistory: () => import('../pages/PointHistory'),
  users: () => import('../pages/Users'),
  calendar: () => import('../pages/Calendar'),
  statistics: () => import('../pages/Statistics'),
  settings: () => import('../pages/Settings'),
  notifications: () => import('../pages/Notifications'),
  media: () => import('../pages/MediaGallery'),
  admin: () => import('../pages/AdminPanel'),
};

/**
 * Preload a specific route by name
 * @param {string} routeName - The name of the route to preload
 * @returns {Promise} - Resolves when the route is preloaded
 */
export const preloadRoute = (routeName) => {
  // Skip if already preloaded
  if (preloadedRoutes.has(routeName)) {
    return Promise.resolve();
  }

  const importFn = routeImports[routeName];
  if (!importFn) {
    console.warn(`Route "${routeName}" not found in preload map`);
    return Promise.resolve();
  }

  // Mark as preloaded immediately to prevent duplicate requests
  preloadedRoutes.add(routeName);

  return importFn().catch((error) => {
    console.error(`Failed to preload route "${routeName}":`, error);
    // Remove from cache so it can be retried
    preloadedRoutes.delete(routeName);
  });
};

/**
 * Preload multiple routes
 * @param {string[]} routeNames - Array of route names to preload
 * @returns {Promise} - Resolves when all routes are preloaded
 */
export const preloadRoutes = (routeNames) => {
  return Promise.all(routeNames.map(preloadRoute));
};

/**
 * Preload routes during browser idle time
 * @param {string[]} routeNames - Array of route names to preload
 * @param {number} timeout - Max time to wait for idle (ms)
 */
export const preloadOnIdle = (routeNames, timeout = 2000) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(
      () => {
        preloadRoutes(routeNames);
      },
      { timeout }
    );
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(() => {
      preloadRoutes(routeNames);
    }, timeout);
  }
};

/**
 * Priority-based preloading
 * Preloads routes in order of priority during idle time
 */
export const preloadByPriority = () => {
  // High priority - Core user flows
  const highPriority = ['home', 'events', 'locations', 'profile'];

  // Medium priority - Frequently accessed
  const mediumPriority = ['calendar', 'leaderboard', 'badges', 'users'];

  // Low priority - Less frequently accessed
  const lowPriority = ['settings', 'notifications', 'statistics', 'media'];

  // Preload high priority immediately on idle
  preloadOnIdle(highPriority, 1000);

  // Preload medium priority after a delay
  preloadOnIdle(mediumPriority, 3000);

  // Preload low priority after longer delay
  preloadOnIdle(lowPriority, 5000);
};

/**
 * Map route paths to route names for preloading
 * @param {string} path - The route path (e.g., "/events")
 * @returns {string|null} - The route name for preloading
 */
export const getRouteNameFromPath = (path) => {
  const pathMap = {
    '/': 'home',
    '/login': 'login',
    '/register': 'register',
    '/locations': 'locations',
    '/events': 'events',
    '/events/new': 'createEvent',
    '/challenges': 'challenges',
    '/challenges/create': 'createChallenge',
    '/profile': 'profile',
    '/leaderboard': 'leaderboard',
    '/badges': 'badges',
    '/point-history': 'pointHistory',
    '/users': 'users',
    '/calendar': 'calendar',
    '/statistics': 'statistics',
    '/settings': 'settings',
    '/notifications': 'notifications',
    '/media': 'media',
    '/admin': 'admin',
  };

  return pathMap[path] || null;
};
